<template>
  <div class="page-wrap">
    <div class="panel-card">

      <!-- Header -->
      <div class="panel-header">
        <div class="panel-header-left">
          <div class="panel-icon"><i class="fa fa-star"></i></div>
          <div>
            <div class="panel-title">
              Feedback Survey Report
              <span class="count-badge" v-if="!load && !dataLoading && reviews.length > 0">
                {{ reviews.length }} review{{ reviews.length !== 1 ? 's' : '' }}
              </span>
            </div>
            <div class="panel-sub">Applicant ratings and feedback grouped by location</div>
          </div>
        </div>
      </div>

      <!-- Filters -->
      <div class="filter-bar">
        <adv-search
          @search="advSearch"
          :is_date_range="true"
          :is_service="false"
          :is_status="false"
          :is_job_report="false"
          :is_location="true"
          :is_admin_sp_location="false"
          :is_admin_sp="false"
          :is_payment_status="true"
          :is_location_appointment="true"
          :has_rating="true"
          :titleworksheet="titleworksheet"
        />
      </div>

      <!-- Initial prompt -->
      <div class="state-box" v-if="load">
        <i class="fa fa-filter"></i>
        Select the search criteria above and click Search
      </div>

      <!-- Loading -->
      <div class="state-box" v-else-if="dataLoading">
        <i class="fa fa-spinner fa-spin"></i>
        Loading feedback…
      </div>

      <!-- Empty -->
      <div class="state-box" v-else-if="reviews.length === 0">
        <i class="fa fa-comment-slash"></i>
        No feedback found for the selected criteria
      </div>

      <!-- Table -->
      <div class="table-wrap" v-else>
        <table class="fb-table">
          <thead>
            <tr>
              <th style="width:40px;">#</th>
              <th style="min-width:130px;">Applicant</th>
              <th style="min-width:90px;">Date</th>
              <th style="min-width:150px;">Service Provider</th>
              <th style="min-width:130px;">Location</th>
              <th style="min-width:130px;">Service</th>
              <th style="min-width:110px;">Rating</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(review, index) in reviews"
              :key="index"
              class="fb-row"
              :class="{ 'row-active': reviewData && reviewData.id === review.id }"
              @click="openModal(review)"
              title="Click to view feedback"
            >
              <td class="col-num">{{ index + 1 }}</td>
              <td class="col-name">{{ review.name }}</td>
              <td class="col-date">{{ formatDate(review.created_at) }}</td>
              <td class="col-sp">{{ review.service_provider_name }}</td>
              <td class="col-loc">{{ review.location_name }}</td>
              <td class="col-svc">{{ review.service_name || '—' }}</td>
              <td class="col-rating">
                <div class="stars">
                  <span
                    v-for="star in 5"
                    :key="star"
                    class="star"
                    :class="{ filled: star <= review.ratings }"
                  >★</span>
                  <span class="rating-num">{{ review.ratings }}/5</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

    </div>

    <!-- Detail modal -->
    <b-modal
      id="review-detail-modal"
      v-model="showReviewModal"
      size="lg"
      centered
      no-close-on-backdrop
      hide-header
    >
      <div class="modal-inner" v-if="reviewData && reviewData.id">

        <!-- Modal header row -->
        <div class="modal-top">
          <div class="modal-applicant">{{ reviewData.name }}</div>
          <div class="modal-stars">
            <span
              v-for="star in 5"
              :key="star"
              class="star star-lg"
              :class="{ filled: star <= reviewData.ratings }"
            >★</span>
            <span class="rating-label">{{ reviewData.ratings }} / 5</span>
          </div>
        </div>

        <!-- Feedback quote -->
        <div class="feedback-quote" v-if="reviewData.feedback">
          <i class="fa fa-quote-left quote-icon"></i>
          <span>{{ reviewData.feedback }}</span>
        </div>
        <div class="no-feedback" v-else>
          No written feedback provided.
        </div>

        <!-- Meta grid -->
        <div class="meta-grid">
          <div class="meta-item">
            <div class="meta-label"><i class="fa fa-calendar-alt"></i> Date</div>
            <div class="meta-val">{{ formatDate(reviewData.created_at) }}</div>
          </div>
          <div class="meta-item">
            <div class="meta-label"><i class="fa fa-building"></i> Service Provider</div>
            <div class="meta-val">{{ reviewData.service_provider_name }}</div>
          </div>
          <div class="meta-item">
            <div class="meta-label"><i class="fa fa-map-marker-alt"></i> Location</div>
            <div class="meta-val">{{ reviewData.location_address }}</div>
          </div>
          <div class="meta-item" v-if="reviewData.service_name">
            <div class="meta-label"><i class="fa fa-fingerprint"></i> Service</div>
            <div class="meta-val">{{ reviewData.service_name }}</div>
          </div>
        </div>

      </div>

      <template #modal-footer>
        <button class="btn-close-modal" @click="closeModal">
          <i class="fa fa-times"></i> Close
        </button>
      </template>
    </b-modal>

  </div>
</template>

<script>
import {
  FETCH_CLIENTS,
  FETCH_FEEDBACK_REPORT
} from "@/core/services/store/actions.type";
import { SET_BREADCRUMB } from "@/core/services/store/mutations.type";
import { mapGetters } from "vuex";
import AdvSearch from "@/includes/search/AdvSearch";

export default {
  components: { AdvSearch },
  computed: {
    ...mapGetters(["currentUser", "getClients", "getSearchDate"])
  },
  data() {
    return {
      search: {
        service: "all",
        date_from: "",
        date_to: "",
        location: "all",
        search_text: ""
      },
      titleworksheet: "",
      breadCrumbs: [
        { id: 1, title: "Dashboard", route: "client.dashboard" },
        { id: 2, title: "Report", route: "" },
        { id: 3, title: "Feedback Survey Report", route: "" }
      ],
      reviews: [],
      header: [],
      load: true,
      dataLoading: false,
      showReviewModal: false,
      reviewData: {}
    };
  },
  mounted() {
    this.$store.dispatch(FETCH_CLIENTS);
    this.$store.commit(SET_BREADCRUMB, this.breadCrumbs);
  },
  methods: {
    advSearch(value) {
      this.dataLoading = true;
      this.load = false;
      const { header, search } = value;
      this.header = header;
      this.search = { ...search };
      search["sp_id"] = this.search.location;
      this.titleworksheet = search.date_from + " - " + search.date_to;

      this.$store.dispatch(FETCH_FEEDBACK_REPORT, search)
        .then(res => {
          this.dataLoading = false;
          this.reviews = res.data;
        })
        .catch(err => {
          this.$toastr.e("Error while fetching feedback report");
          this.dataLoading = false;
          this.msg = err.error;
        });
    },
    formatDate(date) {
      if (!date) return "—";
      const d = new Date(date);
      return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    },
    openModal(review) {
      this.reviewData = review;
      this.showReviewModal = true;
    },
    closeModal() {
      this.showReviewModal = false;
      this.reviewData = {};
    }
  }
};
</script>

<style lang="scss" scoped>
$font: 'Segoe UI', sans-serif;
$blue: #389ad4;
$dark: #0d1b3e;
$border: #dde2ec;
$gold: #f59e0b;

.page-wrap {
  padding: 8px 0 80px;
  font-family: $font;
}

/* ── Panel ── */
.panel-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(13, 27, 62, 0.07);
  overflow: visible;
}

.panel-header {
  display: flex;
  align-items: center;
  padding: 14px 20px;
  background: #fafbfd;
  border-bottom: 1px solid #eef0f6;
  gap: 12px;
  flex-wrap: wrap;
}

.panel-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.panel-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: $gold;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex-shrink: 0;
}

.panel-title {
  font-size: 14px;
  font-weight: 700;
  color: $dark;
  display: flex;
  align-items: center;
  gap: 8px;
}

.count-badge {
  font-size: 11px;
  font-weight: 600;
  color: #7a8499;
  background: #eef0f6;
  border-radius: 10px;
  padding: 1px 7px;
}

.panel-sub {
  font-size: 11px;
  color: #8a94a6;
  margin-top: 2px;
}

.filter-bar {
  padding: 12px 20px;
  border-bottom: 1px solid #f0f3fa;
}

/* ── States ── */
.state-box {
  text-align: center;
  padding: 52px 24px;
  color: #a0aab8;
  font-size: 13px;
  font-family: $font;

  i {
    font-size: 28px;
    display: block;
    margin-bottom: 10px;
    opacity: 0.4;
  }
}

/* ── Table ── */
.table-wrap {
  overflow: auto;
  max-height: 62vh;
}

.fb-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  font-family: $font;
  table-layout: auto;

  thead tr {
    border-bottom: 2px solid #eef0f8;

    th {
      position: sticky;
      top: 0;
      z-index: 2;
      padding: 9px 12px;
      font-size: 11px;
      font-weight: 700;
      color: #7a8499;
      text-transform: uppercase;
      letter-spacing: 0.4px;
      white-space: nowrap;
      text-align: left;
      background: #fff;
      box-shadow: 0 1px 0 #eef0f8;
    }
  }

  tbody tr.fb-row {
    border-bottom: 1px solid #f5f7fa;
    cursor: pointer;
    transition: background 0.15s;

    &:last-child { border-bottom: none; }

    &:hover td { background: #f8fbff; }

    &.row-active td { background: #eef7fb; }

    td {
      padding: 9px 12px;
      color: #2d3748;
      vertical-align: middle;
    }
  }
}

.col-num  { color: #a0aab8; font-size: 12px; width: 40px; }
.col-name { font-weight: 500; color: $dark; }
.col-date { font-size: 12px; color: #6b7280; white-space: nowrap; }
.col-sp   { font-size: 12.5px; color: #4a5568; max-width: 160px; word-break: break-word; }
.col-loc  { font-size: 12.5px; color: #4a5568; max-width: 150px; word-break: break-word; }
.col-svc  { font-size: 12.5px; color: #4a5568; max-width: 150px; word-break: break-word; }

/* ── Stars ── */
.stars {
  display: flex;
  align-items: center;
  gap: 1px;
}

.star {
  font-size: 15px;
  color: #d1d5db;
  line-height: 1;

  &.filled { color: $gold; }
}

.star-lg {
  font-size: 22px;
}

.rating-num {
  font-size: 11px;
  color: #9ca3af;
  margin-left: 5px;
  white-space: nowrap;
}

/* ── Modal ── */
.modal-inner {
  padding: 4px 0;
}

.modal-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
  padding-bottom: 14px;
  border-bottom: 1px solid #eef0f8;
}

.modal-applicant {
  font-size: 16px;
  font-weight: 700;
  color: $dark;
  font-family: $font;
}

.modal-stars {
  display: flex;
  align-items: center;
  gap: 2px;
}

.rating-label {
  font-size: 13px;
  font-weight: 600;
  color: #92400e;
  margin-left: 7px;
}

.feedback-quote {
  background: #fffbeb;
  border-left: 4px solid $gold;
  border-radius: 8px;
  padding: 14px 16px;
  font-size: 13.5px;
  color: #374151;
  font-style: italic;
  line-height: 1.65;
  margin-bottom: 18px;
  position: relative;
}

.quote-icon {
  color: $gold;
  font-size: 14px;
  margin-right: 6px;
  opacity: 0.6;
}

.no-feedback {
  padding: 12px 0;
  color: #9ca3af;
  font-size: 13px;
  font-style: italic;
  margin-bottom: 18px;
}

.meta-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 20px;
}

.meta-item {
  background: #f8fafc;
  border-radius: 8px;
  padding: 10px 14px;
}

.meta-label {
  font-size: 11px;
  font-weight: 700;
  color: #7a8499;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 4px;

  i {
    margin-right: 5px;
    opacity: 0.6;
    font-size: 10px;
  }
}

.meta-val {
  font-size: 13px;
  color: $dark;
  font-weight: 500;
  word-break: break-word;
  line-height: 1.4;
}

.btn-close-modal {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 14px;
  border-radius: 7px;
  border: 1.5px solid #dde2ec;
  background: #fff;
  color: #4a5568;
  font-size: 13px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;
  transition: background 0.15s;

  &:hover { background: #f1f5f9; }
}
</style>
